import { ApiVersions } from '@gonfalon/openapi/api-versions';
import { deleteOAuthConnection } from '@gonfalon/openapi/operations/deleteOAuthConnection';
import { getIntegrationManifest } from '@gonfalon/openapi/operations/getIntegrationManifest';
import { getIntegrationManifests } from '@gonfalon/openapi/operations/getIntegrationManifests';
import { getManifestDynamicEnumOptions } from '@gonfalon/openapi/operations/getManifestDynamicEnumOptions';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';

export const getIntegrationManifestsQuery = createQueryOptions(getIntegrationManifests);

export const getIntegrationManifestQuery = createQueryOptions(getIntegrationManifest);

/**
 * Options for a manifest form field whose choices come from the integration at render time.
 *
 * The route is beta-gated (`mw.RequireBeta`), so the version header is forced rather than left to
 * the caller. Named, and forwarding `context`, because `createQueryOptions` passes React Query's
 * `{ signal }` as the second argument and derives the query-key tag from `fetcher.name` — an
 * anonymous wrapper that takes only `input` drops cancellation.
 */
const getDynamicEnumOptions = (
  input: Parameters<typeof getManifestDynamicEnumOptions>[0],
  context: Parameters<typeof getManifestDynamicEnumOptions>[1] = {},
) => getManifestDynamicEnumOptions({ ...input, apiVersion: ApiVersions.Beta }, context);

export const getDynamicEnumOptionsQuery = createQueryOptions(getDynamicEnumOptions, {
  // `select` unwraps the `options` envelope, matching what `IntegrationAPI` returned.
  select: (data) => data.options ?? [],
});

/**
 * Refetches a manifest and the listing after its OAuth connection changes — both carry
 * `oauth.connected`.
 *
 * Exported because *connecting* has no mutation to hang it off: it is a popup callback
 * (`OAuthLogin.tsx`), not a fetch. Sharing one export with disconnect is what stops the connect path
 * forgetting to invalidate. (Redux updated optimistically here; invalidating costs a round trip but
 * returns the real `_member`.)
 */
export async function invalidateIntegrationManifests(integrationKey: string) {
  const queryClient = getQueryClient();
  await queryClient.invalidateQueries({ queryKey: getIntegrationManifestQuery.partialQueryKey({ integrationKey }) });
  await queryClient.invalidateQueries({ queryKey: getIntegrationManifestsQuery.partialQueryKey() });
}

/** Disconnects an OAuth-backed integration. `providerKey` is the integration key. */
export const useDisconnectGoaltenderOAuth = createMutationHook(deleteOAuthConnection, {
  onSuccess: async (_data, { providerKey }) => invalidateIntegrationManifests(providerKey),
});
